import { Alert, Box, Button, Checkbox, CircularProgress, colors, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid2 as Grid, Radio, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Attachment, drawDocumentPdfPage, Orientation, PageContent, PageSize, paperSizes, type ConfigStateForPDF } from "pdf-generator";
import { PDFDocument, StandardFonts } from 'pdf-lib';
import React from "react";
import { SelectBoxOption } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { useGetDocumentTemplatesDDOptionsQuery, useLazyGetAttachmentUrlQuery } from "../../redux/services";
import { TemplateResponse } from "../../redux/slices/documentTemplate";
import { orientationOptions } from "../../screens/documentTemplates/dashboard/addEditTemplate";
import { blobToBase64 } from "../../utils";
import { jsPDFPaperSizes } from "../dataSheetPDFPreview/pdfView";
import { HighlightSelectBox } from "../highLightSelectBox";
import { SectionPaper } from "../sectionPaper";

import { Viewer, Worker } from "@react-pdf-viewer/core";

// Import styles
import { Download, Settings } from "@mui/icons-material";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { matchIsValidColor, MuiColorInput } from "mui-color-input";
import { closeDialog, openDialog } from "../../redux/slices/dialog";
import moment, { Moment } from "moment";

export const renderingOptions = [{ value: "No Template", label: "No Template" }, { value: "With Template", label: "With Template" }];

export interface PDFGenerationConfig {
    orientation: Orientation;
    paperSize: PageSize;
    enableFooter?: boolean;
    configState: Partial<ConfigStateForPDF>;
}

type GeneratePDFAsync = (config: PDFGenerationConfig) => Promise<string>;

interface ConfigOptions {
    includeMBFormatter?: boolean;
    includeTableHeaderColor?: string;
    include_serial_num: boolean;
    include_revision_table: boolean;
}

interface CustomizeConfig {
    includeConfig?: boolean;
    config?: {
        includeMBFormatter?: { value: boolean, include: boolean };
        includeTableHeaderColor?: { value: string, include: boolean };
        include_serial_num: { value: boolean, include: boolean };
        include_revision_table: { value: boolean, include: boolean };
    };
}

const PDFConfiguration: React.FC<{
    customizeConfig?: CustomizeConfig,
    configState: Partial<ConfigStateForPDF>,
    onApply: (data: Partial<ConfigStateForPDF>) => void
}> = ({ configState, onApply, customizeConfig }) => {
    const dispatch = useAppDispatch();

    const isDisabled = Object.keys(customizeConfig?.config || {}).length <= 0 || Object.values(customizeConfig?.config || {}).every(e => e.include === false);

    const [tableHeaderColor, setHeaderColor] = React.useState('#d1c4e9');
    const [useMBFormatter, setMBFormatter] = React.useState(false);
    const [include_serial_num, setHasSerialNum] = React.useState(false);
    const [include_revision_table, setHasRevisionTable] = React.useState(false);

    React.useEffect(() => {
        setMBFormatter(configState?.useMBFormatter || false)
        setHasSerialNum(configState?.include_serial_num || false)
        setHasRevisionTable(configState?.include_revision_table || false)
        if (matchIsValidColor(configState?.tableHeaderColor || "")) {
            setHeaderColor(configState.tableHeaderColor as string)
        }
        // eslint-disable-next-line
    }, []);

    return (<>
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    width: "100%",
                    fontFamily: 'htrts_medium',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                PDF Configuration
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Stack spacing={3} mt={1}>
                {isDisabled && <Alert severity="info">No Config Enabled</Alert>}
                {customizeConfig?.config?.includeTableHeaderColor?.include &&
                    <MuiColorInput
                        label={"Table Header Color"}
                        size={"small"}
                        fallbackValue={"#d1c4e9"}
                        value={tableHeaderColor}
                        onChange={(color: string) => {
                            if (matchIsValidColor(color)) {
                                setHeaderColor(color)
                            }
                        }}
                    />
                }

                <Grid container spacing={1}>
                    {customizeConfig?.config?.includeMBFormatter?.include &&
                        <Grid
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 12
                            }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useMBFormatter}
                                        onChange={() => {
                                            setMBFormatter(!useMBFormatter)
                                        }}
                                    />
                                }
                                label={"Include Millions & Billions Formatting"}
                            />
                        </Grid>
                    }

                    {customizeConfig?.config?.include_serial_num?.include &&
                        <Grid
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 12
                            }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={include_serial_num}
                                        onChange={() => {
                                            setHasSerialNum(!include_serial_num)
                                        }}
                                    />
                                }
                                label={"Include Reference Number"}
                            />
                        </Grid>
                    }

                    {customizeConfig?.config?.include_revision_table?.include &&
                        <Grid
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 12
                            }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={include_revision_table}
                                        onChange={() => {
                                            setHasRevisionTable(!include_revision_table)
                                        }}
                                    />
                                }
                                label={"Include Revision Table"}
                            />
                        </Grid>
                    }
                </Grid>
            </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
            <Stack sx={{ flexDirection: { xs: "column", md: "row", alignItems: "center", justifyContent: "center", gap: 10 } }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch(closeDialog())}
                >
                    Cancel
                </Button>

                <Button
                    disabled={isDisabled}
                    variant="contained"
                    onClick={() => {
                        const configObj: Partial<ConfigStateForPDF> = {};
                        if (customizeConfig?.config?.includeMBFormatter?.include) {
                            configObj.useMBFormatter = useMBFormatter;
                        }

                        if (customizeConfig?.config?.includeTableHeaderColor?.include) {
                            configObj.tableHeaderColor = tableHeaderColor;
                        }

                        if (customizeConfig?.config?.include_serial_num?.include) {
                            configObj.include_serial_num = include_serial_num;
                        }

                        if (customizeConfig?.config?.include_revision_table?.include) {
                            configObj.include_revision_table = include_revision_table;
                        }
                        onApply(configObj);
                        dispatch(closeDialog());
                    }}
                >
                    Apply Config
                </Button>
            </Stack>
        </DialogActions>
    </>);
};

export const PDFBuilder: React.FC<{
    customizeConfig?: CustomizeConfig,
    title?: string,
    hideHFTemplate?: boolean,
    selectedTemplate?: Partial<TemplateResponse & SelectBoxOption> | null,
    selectedSize?: SelectBoxOption | null,
    selectedOrientation?: SelectBoxOption | null,
    renderOption?: string | null,
    templateData?: PageContent,
    generatePDF: GeneratePDFAsync,
    onSizeChange?: (size: SelectBoxOption | null) => void,
    onOrientationChange?: (orientation: SelectBoxOption | null) => void,
    onTemplateChange?: (template: Partial<TemplateResponse & SelectBoxOption> | null) => void,
    onConfigChange?: (config: Partial<ConfigOptions>) => void,
    disableUpdatingConfig?: boolean
    footerURL?: string,
    hasAttachments?: boolean,
    url?: string,
    infoFooter?: string
}> = ({
    hideHFTemplate = false,
    selectedSize = paperSizes[4],
    selectedOrientation = orientationOptions[0],
    renderOption = renderingOptions[0].value,
    title = '',
    url = '',
    generatePDF = () => '',
    templateData = {
        sequence_id: "",
        version: 0,
        spare_part: false,
        vendor: {
            part_number: "-",
            vendor_name: "-",
            manufacturer_name: "-"
        },
        short_description: "-"
    },
    disableUpdatingConfig = false,
    footerURL = '',
    customizeConfig = {},
    selectedTemplate = null,
    ...props
}) => {
        const defaultLayoutPluginInstance = defaultLayoutPlugin();

        const theme = useTheme();
        const dispatch = useAppDispatch();
        const matches = useMediaQuery(theme.breakpoints.down('md'));
        const templateOptions = useGetDocumentTemplatesDDOptionsQuery({}, { refetchOnFocus: true, refetchOnMountOrArgChange: true, });
        const [getAttachmentUrl] = useLazyGetAttachmentUrlQuery();

        const [pdfInfo, setPdfInfo] = React.useState<string>('');
        const [generatedURL, setURL] = React.useState<string>(url);
        const [building, setBuilding] = React.useState<boolean>(false);
        const [renderingType, setRenderingType] = React.useState<string | null>(renderOption);
        const [paperSize, setPaperSize] = React.useState<SelectBoxOption | null>(selectedSize);
        const [orientation, setOrientation] = React.useState<SelectBoxOption | null>(selectedOrientation)
        const [template, setTemplate] = React.useState<Partial<TemplateResponse & SelectBoxOption> | null>(selectedTemplate);
        const [configState, setConfigState] = React.useState<Partial<ConfigStateForPDF>>({
            tableHeaderColor: customizeConfig?.config?.includeTableHeaderColor?.value ?? "#d1c4e9",
            useMBFormatter: customizeConfig?.config?.includeMBFormatter?.value ?? false,
            include_serial_num: customizeConfig?.config?.include_serial_num.value ?? false,
            include_revision_table: customizeConfig?.config?.include_revision_table?.value ?? false,
        });

        const [shouldFetch, setFetch] = React.useState(false);
    
        const prevAttachmentFetchedTime = React.useRef<Moment | null>(null);

        const options = templateOptions?.data ?? [];

        const handleConfig = () => {
            dispatch(
                openDialog({
                    title: "",
                    hideNegativeBtn: true,
                    hidePositiveBtn: true,
                    enablePadding: false,
                    body: <PDFConfiguration customizeConfig={customizeConfig} configState={configState} onApply={(data) => {
                        if (!disableUpdatingConfig) {
                            setConfigState(data);
                        }
                    }} />,
                })
            );
        };

        const generateHFPdf = async () => {
            setPdfInfo('');
            setBuilding(true);

            const pdfDoc = await PDFDocument.create();
            const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

            const size = (template ? template?.paper_size || "A4" : "A4") as any;

            if (generatedURL && template) {
                // Documents
                try {
                    const pdfBytes = await fetch(decodeURIComponent(generatedURL)).then((res) => res.arrayBuffer())
                    const pdf = await PDFDocument.load(pdfBytes);
                    await drawDocumentPdfPage(pdfDoc, helveticaFont, templateData, pdf, size, template ? template as any : {}, undefined, undefined, true, footerURL)
                } catch (error: any) {
                    console.error(`error while fetching document: ${error}`)
                };
            }

            // Change the title
            pdfDoc.setTitle(title);

            const pdfBytes = await pdfDoc.save();
            const bytes = new Uint8Array(pdfBytes);
            const blob = new Blob([bytes], { type: "application/pdf" });
            const docUrl = URL.createObjectURL(blob);

            const newURL = template ? docUrl : generatedURL;

            await fetch(newURL).then((res) => {
                return res.blob()
            }).then(async (res) => {
                try {
                    const base64 = await blobToBase64(res) as string;
                    setPdfInfo(base64);
                } catch (error) {
                    console.error(`Error loading on preview: ${error}`)
                }
            }).catch((error: any) => {
                console.error(`Error loading on preview: ${error}`)
            }).finally(() => {
                setBuilding(false);
            })
        };

        const prepareURL = async () => {
            setBuilding(true);
            let url = await generatePDF({
                orientation: template ? (template.orientation ?? "portrait") : (orientation?.value || "portrait") as Orientation,
                paperSize: template ? (template.paper_size ?? "A4") as PageSize : (paperSize?.value || "A4") as PageSize,
                enableFooter: template ? false : true,
                configState
            });
            setURL(url);
            setBuilding(false);
        };

        const handleDownload = (url: string, title: string) => {
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = `${title}.pdf`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        };

        const getConfigFromProps = () => {
            const report_config: any = customizeConfig?.config ?? {};

            const obj: Partial<ConfigStateForPDF> = {};

            if (report_config.include_serial_num.include) {
                obj.include_serial_num = report_config?.include_serial_num.value ?? false;
            }

            if (report_config.includeMBFormatter?.include) {
                obj.useMBFormatter = report_config?.includeMBFormatter?.value ?? false;
            }

            if (report_config.includeTableHeaderColor?.include) {
                obj.tableHeaderColor = report_config?.includeTableHeaderColor?.value ?? "#d1c4e9";
            }

            if (report_config.include_revision_table?.include) {
                obj.include_revision_table = report_config?.include_revision_table.value ?? false;
            }

            return obj;
        };

        React.useEffect(() => {
            if (props.onTemplateChange) {
                prepareURL();
                props.onTemplateChange(template);
            }
            // eslint-disable-next-line
        }, [JSON.stringify(template)]);

        React.useEffect(() => {
            if (props.onOrientationChange && orientation?.value !== selectedOrientation?.value) {
                prepareURL();
                props.onOrientationChange(orientation);
            }
            // eslint-disable-next-line
        }, [orientation]);

        React.useEffect(() => {
            if (props.onSizeChange && paperSize?.value !== selectedSize?.value) {
                prepareURL();
                props.onSizeChange(paperSize);
            }
            // eslint-disable-next-line
        }, [paperSize]);

        React.useEffect(() => {

            const obj = getConfigFromProps();

            if (props.onConfigChange && (
                obj.include_serial_num !== configState.include_serial_num
                || obj.tableHeaderColor !== configState.tableHeaderColor
                || obj.useMBFormatter !== configState.useMBFormatter
                || obj.include_revision_table !== configState.include_revision_table
            )) {
                prepareURL();
                props.onConfigChange(configState);
            }
            // eslint-disable-next-line
        }, [configState]);

        React.useEffect(() => {
            if (generatedURL) {
                generateHFPdf();
            }
            // eslint-disable-next-line
        }, [generatedURL]);

        React.useEffect(() => {
            const obj = getConfigFromProps();
            setConfigState((prevState) => ({ ...prevState, ...obj }))
            // eslint-disable-next-line
        }, [JSON.stringify(customizeConfig?.config)]);

        React.useEffect(() => {
            let attachment = template?.attachments?.at(-1);
            if (attachment?._id) {
                if (moment().diff(moment(prevAttachmentFetchedTime.current), 'minutes') > 2 || prevAttachmentFetchedTime.current === null) {
                    getAttachmentUrl({ id: attachment?._id, object_type: "hfTemplate", object_id: template?._id })
                        .unwrap()
                        .then((res) => {
                            if (res) {
                                setTemplate((prev) => ({ ...prev, attachments: prev?.attachments ? [{...prev?.attachments.at(-1), url: res}] as Attachment[] : [] }))
                            }
                        })
                        .catch((error) => {
                            console.error(`Error fetching document image: ${JSON.stringify(error)}`);
                        }).finally(() => {
                            prevAttachmentFetchedTime.current = moment();
                        });
                }
            }
            // eslint-disable-next-line
        }, [selectedTemplate?._id, template?._id, shouldFetch])
    
        React.useEffect(() => {
            let interval: NodeJS.Timeout;
            if (selectedTemplate?._id || template?._id) {
                interval = setInterval(() => {
                   setFetch((prev) => !prev);
                }, 2000);
            }
    
            return () => {
                if (selectedTemplate?._id || template?._id) {
                    clearInterval(interval);
                }
            };
            // eslint-disable-next-line
        }, [selectedTemplate?._id, template?._id])

        return (
            <Box height={"100%"}>
                <SectionPaper stack title="Configure Preview" renderButtons={[
                    (customizeConfig?.includeConfig && !disableUpdatingConfig) ? <Button size="small" variant={"outlined"} onClick={handleConfig} startIcon={<Settings />}>Apply Config</Button> : <></>,
                    <Button variant="contained" onClick={() => handleDownload(pdfInfo, title)} size="small" startIcon={<Download />}>Download</Button>
                ]}>
                    <Grid container spacing={1} width={"100%"} columns={13}>
                        <Grid
                            size={{
                                xs: 13,
                                md: 6
                            }}>
                            <Stack spacing={2}>
                                <FormControlLabel
                                    disabled={disableUpdatingConfig}
                                    value={renderingOptions[0].value}
                                    control={
                                        <Radio
                                            size="small"
                                            checked={renderingOptions[0]?.value === renderingType}
                                            onChange={() => {
                                                setRenderingType(renderingOptions[0].value);
                                                setTemplate(null);
                                            }}
                                            value={renderingOptions[0].value} />
                                    }
                                    label={renderingOptions[0].label}
                                />
                                <HighlightSelectBox
                                    disabled={renderingOptions[1]?.value === renderingType || disableUpdatingConfig}
                                    label={"Paper Size"}
                                    margin={"none"}
                                    fullWdith
                                    value={paperSize}
                                    options={jsPDFPaperSizes}
                                    onChange={(option: SelectBoxOption) => { setPaperSize(option) }}
                                />
                                <HighlightSelectBox
                                    disabled={renderingOptions[1]?.value === renderingType || disableUpdatingConfig}
                                    label={"Orientation"}
                                    margin={"none"}
                                    fullWdith
                                    value={orientation}
                                    options={orientationOptions}
                                    onChange={(option: SelectBoxOption) => { setOrientation(option) }}
                                />
                            </Stack>
                        </Grid>

                        {!hideHFTemplate && <>
                            <Grid
                                mt={2}
                                size={{
                                    xs: 13,
                                    md: 1
                                }}>
                                <Stack sx={{ width: "100%", height: "100%" }} justifyContent={matches ? "center" : "flex-start"} alignItems={matches ? "stretch" : 'center'}>
                                    <Divider orientation={matches ? "horizontal" : "vertical"} />
                                </Stack>
                            </Grid>

                            <Grid
                                size={{
                                    xs: 13,
                                    md: 6
                                }}>
                                <Stack spacing={2}>
                                    <FormControlLabel
                                        disabled={disableUpdatingConfig}
                                        value={renderingOptions[1].value}
                                        control={
                                            <Radio
                                                size="small"
                                                checked={renderingOptions[1].value === renderingType}
                                                onChange={() => {
                                                    setRenderingType(renderingOptions[1].value);
                                                    setPaperSize(paperSizes[4]);
                                                    setOrientation(orientationOptions[0]);
                                                }}
                                                value={renderingOptions[1].value}
                                                name="radio-buttons"
                                            />
                                        }
                                        label={renderingOptions[1].label}
                                    />

                                    <HighlightSelectBox
                                        disabled={renderingOptions[0]?.value === renderingType || disableUpdatingConfig}
                                        label={"Select template"}
                                        margin={"none"}
                                        fullWdith
                                        value={template as SelectBoxOption}
                                        options={options}
                                        onChange={(option: SelectBoxOption) => { setTemplate(option) }}
                                    />

                                    <Alert variant="outlined" severity="warning" sx={{ borderWidth: 6, borderRight: 0, borderTop: 0, borderBottom: 0, mt: 2 }}>
                                        <Typography variant="caption" color={colors.grey[500]}>
                                            Caution! Please ensure the table width is not exceeding the selected template page width!
                                            Please select appropriate template to accommodate table content.
                                        </Typography>
                                    </Alert>
                                </Stack>
                            </Grid>
                        </>}
                    </Grid>
                </SectionPaper>

                <Stack mt={2} flexGrow={1} overflow={"auto"} id={"viewer"} height={"100%"} width={"100%"}>
                    {pdfInfo && !building &&
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <div style={{ width: "100%", height: "100%" }}>
                                <Viewer fileUrl={pdfInfo} plugins={[defaultLayoutPluginInstance]} />
                            </div>
                        </Worker>
                    }
                    {(!pdfInfo || building) && <Stack height={"100%"} alignItems={'center'} justifyContent="center"><CircularProgress /></Stack>}
                </Stack>

                {props.infoFooter && <Typography variant='caption' color='textSecondary'>{props.infoFooter}</Typography>}
            </Box>
        );
    }